import React, { useState, useMemo } from "react";
import { AddWorkHistory } from "./AddWorkHistory";
import { AddVolunteerExperience } from "./AddVolunteerExperience";
import { IWorkHistory } from "features/settings/types";
import { Popconfirm, Skeleton, Alert } from "antd";
import { useDelete } from "hooks/useDelete";
import { QUERY_KEY_FOR_TALENT_PROFILE } from "features/settings/hooks/useGetTalentProfile";
import { ProfileNavigationButtons } from "./ProfileNavigationButtons";
import { useNavigate } from "react-router-dom";
import { getNextSection } from "../../services/profileNavigationService";
import { useQueryClient } from "react-query";
import { useProfileCompletion } from "features/settings/hooks/useProfileCompletion";

const deleteEndPointUrl = "delete-work-history/";
const deleteVolunteerEndPointUrl = "delete-volunteer-experience/";

const parseAchievementsList = (raw?: string): string[] => {
  if (!raw) return [];
  const trimmed = raw.trim();
  if (!trimmed || trimmed === "[]" || trimmed === "null") return [];

  if (trimmed.startsWith("[")) {
    try {
      const parsed = JSON.parse(trimmed);
      if (Array.isArray(parsed)) {
        return parsed.map((item) => String(item).trim()).filter(Boolean);
      }
    } catch {
      // fall through to newline splitting
    }
  }

  return trimmed
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => line.replace(/^[★•\-]\s*/, ""));
};

const hasAchievementsInDescription = (description: string): boolean => {
  if (!description) return false;

  const achievementIndicators = [
    /achievements?/i,
    /accomplishments?/i,
    /results?/i,
    /increased/i,
    /reduced/i,
    /improved/i,
    /launched/i,
    /delivered/i,
    /achieved/i,
    /boosted/i,
    /streamlined/i,
  ];

  return achievementIndicators.some((pattern) => pattern.test(description));
};

const extractResponsibilitiesAndAchievements = (
  description: string,
  achievements?: string,
) => {
  const responsibilities: string[] = [];

  if (description) {
    const lines = description.split("\n");
    for (const line of lines) {
      const trimmedLine = line.trim();
      if (!trimmedLine) continue;

      let cleanLine = trimmedLine;
      if (
        cleanLine.startsWith("-") ||
        cleanLine.startsWith("•") ||
        /^\d+\./.test(cleanLine)
      ) {
        cleanLine = cleanLine.replace(/^[-•\d.]+\s*/, "");
      }
      if (cleanLine) {
        responsibilities.push(cleanLine);
      }
    }

    if (responsibilities.length === 0 && description) {
      responsibilities.push(description);
    }
  }

  const achievementsList: string[] = parseAchievementsList(achievements);

  return { responsibilities, achievements: achievementsList };
};

const isWorkHistoryComplete = (item: IWorkHistory): boolean => {
  const hasDescription = item.description && item.description.trim().length > 0;

  return !!(
    item.company &&
    item.company.trim().length > 0 &&
    item.title &&
    item.title.trim().length > 0 &&
    item.sector_id &&
    item.start_date &&
    (item.is_current === 1 ||
      (item.end_date && item.end_date !== "1970-01-01")) &&
    hasDescription
  );
};

const isVolunteerComplete = (item: IWorkHistory): boolean => {
  return !!(
    item.company &&
    item.company.trim().length > 0 &&
    item.title &&
    item.title.trim().length > 0 &&
    item.start_date &&
    (item.is_current === 1 || (item.end_date && item.end_date !== "1970-01-01"))
  );
};

export const WorkHistory: React.FC<{
  data: IWorkHistory[];
  volunteer?: IWorkHistory[];
  isLoading?: boolean;
}> = ({ data, volunteer = [], isLoading }) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { refetchProfile } = useProfileCompletion();

  const volunteerData =
    volunteer.length > 0
      ? volunteer
      : data.filter(
          (item) =>
            item.type?.toLowerCase() === "volunteer" ||
            item.type === "volunteer" ||
            item.type === "Volunteer",
        );

  const workData = data.filter(
    (item) =>
      item.type?.toLowerCase() !== "volunteer" &&
      item.type !== "volunteer" &&
      item.type !== "Volunteer",
  );

  const hasCompleteExperience = useMemo(() => {
    const hasCompleteWork = workData.some((item) =>
      isWorkHistoryComplete(item),
    );
    const hasCompleteVolunteer = volunteerData.some((item) =>
      isVolunteerComplete(item),
    );
    return hasCompleteWork || hasCompleteVolunteer;
  }, [workData, volunteerData]);

  const [addHistory, setAddHistory] = useState(false);
  const [addVolunteer, setAddVolunteer] = useState(false);
  const [volunteerId, setVolunteerId] = useState<number>();
  const [workId, setWorkId] = useState<number>();

  const { removeData } = useDelete({
    queryKey: QUERY_KEY_FOR_TALENT_PROFILE,
    deleteEndPointUrl,
  });

  const { removeData: removeVolunteerData } = useDelete({
    queryKey: QUERY_KEY_FOR_TALENT_PROFILE,
    deleteEndPointUrl: deleteVolunteerEndPointUrl,
  });

  const handleRemoveWork = (id: number) => {
    removeData(id);
    queryClient.invalidateQueries([QUERY_KEY_FOR_TALENT_PROFILE]);
    setTimeout(() => refetchProfile(), 100);
  };

  const handleRemoveVolunteer = (id: number) => {
    removeVolunteerData(id);
    queryClient.invalidateQueries([QUERY_KEY_FOR_TALENT_PROFILE]);
    setTimeout(() => refetchProfile(), 100);
  };

  const handleWorkClose = () => {
    setAddHistory(false);
    setWorkId(undefined);
    queryClient.invalidateQueries([QUERY_KEY_FOR_TALENT_PROFILE]);
    setTimeout(() => refetchProfile(), 100);
  };

  const handleVolunteerClose = () => {
    setAddVolunteer(false);
    setVolunteerId(undefined);
    queryClient.invalidateQueries([QUERY_KEY_FOR_TALENT_PROFILE]);
    setTimeout(() => refetchProfile(), 100);
  };

  const handleWork = (id: number) => {
    setWorkId(id);
    setAddHistory(true);
  };

  const handleVolunteer = (id: number) => {
    setVolunteerId(id);
    setAddVolunteer(true);
  };

  const handleNext = () => {
    const nextSection = getNextSection(window.location.pathname);
    if (nextSection) {
      navigate(nextSection.link);
    }
  };

  const hasAchievements = (item: IWorkHistory) => {
    return parseAchievementsList(item.achievements).length > 0;
  };

  return (
    <>
      <AddWorkHistory
        id={workId}
        open={addHistory}
        handleClose={handleWorkClose}
      />

      <AddVolunteerExperience
        id={volunteerId}
        open={addVolunteer}
        handleClose={handleVolunteerClose}
      />

      <div className="mt-6 max-w-5xl">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h4 className="text-base font-semibold text-primary">
              Work Experience
            </h4>
            <p className="text-sm text-gray-500">
              Add your employment background
            </p>
          </div>

          <button
            onClick={() => {
              setWorkId(undefined);
              setAddHistory(true);
            }}
            className="bg-primary text-white text-sm font-medium px-4 py-2 rounded-md flex items-center gap-1 hover:bg-primary/90"
          >
            <i className="ri-add-line"></i>
            <span>Add New</span>
          </button>
        </div>

        {workData.length === 0 && volunteerData.length === 0 && (
          <Alert
            message="Experience Required"
            description="Please add at least one complete work experience or volunteer experience to continue."
            type="error"
            showIcon
            className="mb-4 bg-red-50 border-red-200"
          />
        )}

        <Skeleton active loading={isLoading}>
          <div className="space-y-4 mb-10">
            {workData.length > 0 ? (
              workData.map((item) => {
                const { responsibilities, achievements } =
                  extractResponsibilitiesAndAchievements(
                    item.description || "",
                    item.achievements,
                  );
                const missingAchievements = !hasAchievements(item);
                const isComplete = isWorkHistoryComplete(item);

                return (
                  <div
                    key={item.id}
                    className={`border rounded-lg overflow-hidden transition ${
                      !isComplete
                        ? "border-red-400 border-2 bg-red-50/5"
                        : "border-gray-200 hover:border-blue-500"
                    }`}
                  >
                    <div className="px-4 py-4 border-b border-gray-100">
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className="text-xs font-medium text-gray-500">
                              {item.company}
                            </p>
                            {!isComplete && (
                              <span className="text-xs bg-red-100 text-red-600 px-2 py-0.5 rounded-full">
                                Incomplete
                              </span>
                            )}
                          </div>
                          <h4 className="text-base font-semibold text-primary mt-1">
                            {item.title}
                          </h4>
                        </div>

                        <div className="flex items-center gap-3">
                          <i
                            onClick={() => handleWork(item.id)}
                            className="ri-pencil-fill text-gray-400 cursor-pointer text-xl hover:text-primary"
                          ></i>

                          <Popconfirm
                            okButtonProps={{
                              style: {
                                backgroundColor: "#f5222d",
                                color: "#ffffff",
                              },
                            }}
                            title="Delete work history"
                            description="Are you sure to delete this work history?"
                            onConfirm={() => handleRemoveWork(item.id)}
                          >
                            <i className="ri-delete-bin-line text-gray-400 cursor-pointer text-xl hover:text-red-600"></i>
                          </Popconfirm>
                        </div>
                      </div>

                      <div className="flex items-center gap-2 mt-3 text-sm text-gray-500">
                        <i className="ri-time-line"></i>
                        <span>
                          {item.start_date
                            ? new Date(item.start_date).toLocaleDateString(
                                "en-GB",
                                { month: "short", year: "numeric" },
                              )
                            : "N/A"}{" "}
                          {item.is_current === 1
                            ? " - Currently working here"
                            : item.end_date &&
                              item.end_date !== "1970-01-01" && (
                                <>
                                  {" "}
                                  -{" "}
                                  {new Date(item.end_date).toLocaleDateString(
                                    "en-GB",
                                    { month: "short", year: "numeric" },
                                  )}
                                </>
                              )}
                        </span>
                      </div>
                    </div>

                    {responsibilities.length > 0 && (
                      <div className="px-4 py-3 border-b border-gray-50">
                        <h5 className="text-sm font-semibold text-gray-700 mb-2">
                          Responsibilities
                        </h5>
                        <div className="space-y-1">
                          {responsibilities.map((resp, idx) => (
                            <div
                              key={idx}
                              className="flex items-start text-sm text-gray-600"
                            >
                              <span className="text-primary mr-2">•</span>
                              <span>{resp}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    <div className="px-4 py-3">
                      {!missingAchievements && achievements.length > 0 && (
                        <>
                          <h5 className="text-sm font-semibold text-gray-700 mb-2">
                            Achievements
                          </h5>
                          <div className="space-y-1">
                            {achievements.map((achievement, idx) => (
                              <div
                                key={idx}
                                className="flex items-start text-sm text-gray-600"
                              >
                                <span className="text-yellow-500 mr-2">★</span>
                                <span>{achievement}</span>
                              </div>
                            ))}
                          </div>
                        </>
                      )}

                      {missingAchievements && (
                        <Alert
                          message="Add your achievements"
                          description={
                            <span>
                              To improve your profile strength and increase your
                              chances of being discovered by recruiters, kindly
                              add your key achievements in this role.
                            </span>
                          }
                          type="warning"
                          showIcon
                          className="bg-yellow-50 border-yellow-200"
                        />
                      )}
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="text-center py-8 text-gray-500">
                No work experience added yet
              </div>
            )}
          </div>
        </Skeleton>

        {/* Volunteer Experience Section */}
        <div className="border-t pt-6">
          <div className="flex items-start justify-between mb-4">
            <div>
              <h4 className="text-base font-semibold text-blue-600">
                Volunteer Experience
              </h4>
              <p className="text-sm text-gray-500">
                Share your volunteer work and community involvement
              </p>
            </div>

            <button
              onClick={() => {
                setVolunteerId(undefined);
                setAddVolunteer(true);
              }}
              className="bg-blue-600 text-white text-sm font-medium px-4 py-2 rounded-md flex items-center gap-1 hover:bg-blue-700"
            >
              <i className="ri-add-line"></i>
              <span>Add New</span>
            </button>
          </div>

          <Skeleton active loading={isLoading}>
            <div className="space-y-4">
              {volunteerData.length > 0 ? (
                volunteerData.map((item) => {
                  const { responsibilities, achievements } =
                    extractResponsibilitiesAndAchievements(
                      item.description || "",
                      item.achievements,
                    );
                  const missingAchievements = !hasAchievements(item);
                  const isComplete = isVolunteerComplete(item);

                  return (
                    <div
                      key={item.id}
                      className={`border rounded-lg overflow-hidden transition ${
                        !isComplete
                          ? "border-red-400 border-2 bg-red-50/5"
                          : "border-blue-200 bg-blue-50/30 hover:border-blue-400"
                      }`}
                    >
                      <div className="px-4 py-4 border-b border-blue-100">
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 flex-wrap">
                              <p className="text-xs font-medium text-blue-600">
                                {item.company}
                              </p>
                              {!isComplete && (
                                <span className="text-xs bg-red-100 text-red-600 px-2 py-0.5 rounded-full">
                                  Incomplete
                                </span>
                              )}
                            </div>
                            <h4 className="text-base font-semibold text-blue-800 mt-1">
                              {item.title}
                              <span className="ml-2 text-xs bg-blue-100 text-blue-600 px-2 py-0.5 rounded-full">
                                Volunteer
                              </span>
                            </h4>
                          </div>

                          <div className="flex items-center gap-3">
                            <i
                              onClick={() => handleVolunteer(item.id)}
                              className="ri-pencil-fill text-gray-400 cursor-pointer text-xl hover:text-blue-600"
                            ></i>

                            <Popconfirm
                              okButtonProps={{
                                style: {
                                  backgroundColor: "#f5222d",
                                  color: "#ffffff",
                                },
                              }}
                              title="Delete volunteer experience"
                              description="Are you sure to delete this volunteer experience?"
                              onConfirm={() => handleRemoveVolunteer(item.id)}
                            >
                              <i className="ri-delete-bin-line text-gray-400 cursor-pointer text-xl hover:text-red-600"></i>
                            </Popconfirm>
                          </div>
                        </div>

                        <div className="flex items-center gap-2 mt-3 text-sm text-gray-500">
                          <i className="ri-time-line"></i>
                          <span>
                            {item.start_date
                              ? new Date(item.start_date).toLocaleDateString(
                                  "en-GB",
                                  { month: "short", year: "numeric" },
                                )
                              : "N/A"}{" "}
                            {item.is_current === 1
                              ? " - Currently volunteering here"
                              : item.end_date &&
                                item.end_date !== "1970-01-01" && (
                                  <>
                                    {" "}
                                    -{" "}
                                    {new Date(item.end_date).toLocaleDateString(
                                      "en-GB",
                                      { month: "short", year: "numeric" },
                                    )}
                                  </>
                                )}
                          </span>
                        </div>
                      </div>

                      {responsibilities.length > 0 && (
                        <div className="px-4 py-3 border-b border-blue-100">
                          <h5 className="text-sm font-semibold text-blue-700 mb-2">
                            Responsibilities
                          </h5>
                          <div className="space-y-1">
                            {responsibilities.map((resp, idx) => (
                              <div
                                key={idx}
                                className="flex items-start text-sm text-gray-600"
                              >
                                <span className="text-blue-500 mr-2">•</span>{" "}
                                <span>{resp}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      <div className="px-4 py-3">
                        {!missingAchievements && achievements.length > 0 && (
                          <>
                            <h5 className="text-sm font-semibold text-blue-700 mb-2">
                              Contributions
                            </h5>
                            <div className="space-y-1">
                              {achievements.map((achievement, idx) => (
                                <div
                                  key={idx}
                                  className="flex items-start text-sm text-gray-600"
                                >
                                  <span className="text-blue-500 mr-2">✦</span>
                                  <span>{achievement}</span>
                                </div>
                              ))}
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="text-center py-8 text-gray-500">
                  No volunteer experience added yet
                </div>
              )}
            </div>
          </Skeleton>
        </div>

        <div className="mt-6">
          <ProfileNavigationButtons
            hasForm={false}
            onNext={handleNext}
            nextButtonLabel="Next"
            disabled={!hasCompleteExperience}
          />
        </div>
      </div>
    </>
  );
};
